// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import { ProductListResponseDto } from '@epicuro-next/data/engagement/engagement-models';
import {
  EventHandlerPayloadResolveMoment,
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { mergeMultipleConfigs } from '@epicuro-next/platform/dynamic-components-utils';

import { createInstanceWithChildren } from '../../../utils/dynamic-items-utils';
import { ScrollableRecommendationPanelBuilder } from '../../builders/scrollable-recommendation-panel-builder';
import { createLink } from '../../components/link';
import { configStripSetProductSource, trackListAction } from '../../events';
import { DEFAULT_ROOT_PROPERTIES } from '../../properties/default-root-properties';
import { DEFAULT_ROOT_STYLES } from '../../properties/default-root-styles';
import { PANEL_STYLING_PROPERTIES } from '../../properties/panel-styling-properties';
import { PANEL_TRACKING_PROPERTIES } from '../../properties/panel-tracking-properties';
import { PRODUCT_ITEM_VIEW_PROPERTIES } from '../../properties/product-item-view-properties';
import { SCROLLABLE_PANEL_PROPERTIES } from '../../properties/scrollable-panel-properties';
import { SEE_MORE_LINK_PROPERTIES } from '../../properties/see-more-link-properties';
import { SHOW_PANEL } from '../../properties/show-panel-property';
import { getListWithPBasketSearchResultsProvider } from '../../providers/list-with-p-basket-search-results-provider';
import { SingleProductDataType } from '../../types/single-product-data-type';

function createHorizontalScrollablePanelFromList() {
  const root = createInstanceWithChildren({
    id: 'root',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      ...DEFAULT_ROOT_STYLES,
    },
    data: {
      ...DEFAULT_ROOT_PROPERTIES,
      ...SCROLLABLE_PANEL_PROPERTIES,
      ...PANEL_TRACKING_PROPERTIES,
      ...PANEL_STYLING_PROPERTIES,
      ...SEE_MORE_LINK_PROPERTIES,
      ...PRODUCT_ITEM_VIEW_PROPERTIES,
      listId: 'SPECIFY_LIST_ID_HERE',
      listData: getListWithPBasketSearchResultsProvider({
        provider: 'self',
        property: 'listId',
      }),
      panelName: 'horizontal-scrollable-panel-from-list',
      panelTrackingId: 'panel-style-not-configured-yet',
      displaySeeMoreLink: true,
      seeMoreLinkText: recommendationsPanelsLocaleKeys.panelSeeMoreButton,

      showProductTitle: true,
      showProductCardIcons: true,
      showProductPrices: true,
      productData: {
        provider: 'id:root',
        property: 'listData',
        transform: function (data: { productData: SingleProductDataType[] }) {
          return data.productData;
        }.toString(),
      },
      headerText: {
        provider: 'id:root',
        property: 'listData',
        transform: function (data: {
          listData: ProductListResponseDto;
        }): string {
          if (!('listData' in data) || !('name' in data.listData)) {
            return 'Name of list was not found.';
          }

          return data.listData.name;
        }.toString(),
      },

      ...SHOW_PANEL,
    },
    children: [],
  });

  const seeMoreLink = createSeeMoreLinkElement({
    displayLink: {
      provider: 'id:root',
      property: 'displaySeeMoreLink',
      transform: function (
        displaySeeMoreLink?: boolean | string | null,
      ): boolean {
        if (displaySeeMoreLink === undefined || displaySeeMoreLink === null) {
          return false;
        }

        if (typeof displaySeeMoreLink === 'string') {
          displaySeeMoreLink =
            displaySeeMoreLink.trim().toLowerCase() === 'true';
        }

        return displaySeeMoreLink;
      }.toString(),
    },
    text: {
      provider: 'id:root',
      property: 'seeMoreLinkText',
    },
    listId: {
      provider: 'id:root',
      property: 'listId',
    },
    style: {
      provider: 'id:root',
      property: 'seeMoreLinkStyle',
    },
    panelName: {
      provider: 'id:root',
      property: 'panelName',
    },
    panelTrackingId: {
      provider: 'id:root',
      property: 'panelTrackingId',
    },
  });

  const footer = createInstanceWithChildren({
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['footer', 'd-flex', 'flex-row', 'w-100', 'position-relative'],
    },
    children: [seeMoreLink],
  });

  const horizontalScrollablePanel =
    new ScrollableRecommendationPanelBuilder().build();

  const panelWithOverwrittenProperties = mergeMultipleConfigs(
    horizontalScrollablePanel.scrollablePanel,
    [
      {
        id: 'footer-container',
        if: ['EQ', [true, true]],
        children: [footer],
      },
    ],
  ) as IDynamicItem[];

  root.children.push(panelWithOverwrittenProperties[0]);

  const overwrittenRoot = mergeMultipleConfigs(root, [
    {
      id: 'root',
      styles: {
        variables: {
          '--product-card-icons-font-size': '0.6rem',
          '--product-card-icons-gender-icon-font-size': '0.75rem',
          '--product-card-icons-gender-icon-margin-right': '0.5rem',
          '--product-price-title-font-size': '0.5rem',
          '--product-price-value-font-size': '0.8rem',
          '--product-price-value-color': '#222222',
          '--product-price-title-color': '#777777',
          '--product-card-icons-flex-direction': 'column',
          '--product-card-icons-align-items': 'start',
        },
      },
    },
  ])[0];

  return { overwrittenRoot, horizontalScrollablePanel };
}

export function createSeeMoreLinkElement(
  dataProviders: {
    displayLink: IDynamicBindableValue | IDynamicBindableValueCombination;
    text: IDynamicBindableValue | IDynamicBindableValueCombination;
    listId: IDynamicBindableValue;
    style: IDynamicBindableValue;
    panelName: IDynamicBindableValue;
    panelTrackingId: IDynamicBindableValue;
  },
  textStyle: Record<
    string,
    string | IDynamicBindableValue | IDynamicBindableValueCombination
  > = {},
) {
  const linkContent: IDynamicSimpleText = {
    id: 'see-more-link-content',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: textStyle,
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: dataProviders.text,
    },
  };

  const routeProvider = {
    values: [
      dataProviders.listId,
      {
        provider: 'GlobalStoreProviderService',
        property: {
          path: 'router.state.url',
        },
        filter: function (path: string | null | undefined): boolean {
          return (
            path !== undefined && path !== null && typeof path === 'string'
          );
        }.toString(),
        transform: function (url: string): string {
          return url;
        }.toString(),
      },
    ],
    transform: function ([listId, currentPath]: [string, string]): string {
      const currentUrl = new URL(location.href);
      const defaultRootPath = currentUrl.pathname.replace(currentPath, '');
      let route = `${defaultRootPath}/browse/product-lists/${listId}/details`;
      if (route.startsWith(defaultRootPath)) {
        route = route.replace(defaultRootPath, '');
      }

      return route;
    }.toString(),
  };

  const seeMoreLink = createLink('see-more-link', routeProvider, linkContent);

  seeMoreLink.if = ['EQ', [dataProviders.displayLink, true]];

  seeMoreLink.eventHandlers = {
    onClick: [
      trackListAction({
        listIdProvider: dataProviders.listId,
      }),
      configStripSetProductSource(
        {
          listIdProvider: dataProviders.listId,
          panelNameProvider: dataProviders.panelName,
          panelTrackingIdProvider: dataProviders.panelTrackingId,
        },
        {
          seeMore: true,
          payloadFetchMoment:
            EventHandlerPayloadResolveMoment.BeforeAddEventListener,
        },
      ),
    ],
  };

  seeMoreLink.styles.style = {
    __multiple__: dataProviders.style,
  };
  seeMoreLink.styles.classes.push('see-more-link');

  return seeMoreLink;
}

export const horizontalScrollablePanelFromList =
  createHorizontalScrollablePanelFromList().overwrittenRoot;
